<template lang="pug">
img(
  v-if="!!image.sourceUrl && lazy"
  :src="replaceUrlS3(image.sourceUrl, $config.public)"
  :alt="extractAltText"
  loading="lazy"
  :data-srcset="image.srcSet"
  v-bind="imageAttrs")
img(
  v-else-if="!!image.sourceUrl && !lazy && image.srcSet"
  :src="replaceUrlS3(image.sourceUrl, $config.public)"
  :alt="extractAltText"
  :srcset="image.srcSet"
  :sizes="image.sizes"
  v-bind="imageAttrs")
img(
  v-else-if="!!image.sourceUrl && !lazy && !image.srcSet"
  :src="replaceUrlS3(image.sourceUrl, $config.public)"
  :alt="extractAltText"
  v-bind="imageAttrs")
img(
  v-else-if="lazy"
  :src="replaceUrlS3(imageData.url, $config.public)"
  :alt="extractAltText"
  loading="lazy"
  v-bind="imageAttrs")
img(
  v-else
  :src="replaceUrlS3(imageData.url, $config.public)"
  :alt="extractAltText"
  v-bind="imageAttrs")

</template>

<script lang="ts">
import { defineComponent } from "vue"
import { replaceUrlS3 } from "~/utils/imageUrlS3"
import { getMediaById } from "~/services/public/Pages"

interface ImageSize {
  name?: string
  width: number
  height: number
  sourceUrl: string
}

interface ImageData {
  width: number | string
  height: number | string
  url: string
  id?: any
  sourceUrl?: string
  srcSet: string | null
  altText?: string
  mediaDetails: {
    width: number
    height: number
    sizes?: ImageSize[]
  }
  sizes?: ImageSize[]
}

interface ComponentData {
  image: ImageData
}

interface Block {
  innerHtml?: string
  [key: string]: any
}

export default defineComponent({
  name: "OptimizedImage",

  props: {
    blockId: {
      type: String,
      default: ""
    },
    media: {
      type: String,
      default: ""
    },
    imageData: {
      type: Object as () => ImageData,
      required: true
    },
    lazy: {
      type: Boolean,
      default: false
    },
    idType: {
      type: String,
      default: "DATABASE_ID"
    },
    size: {
      type: String,
      default: null
    },
    block: {
      type: Object as () => Block,
      default: null
    }
  },

  data(): ComponentData {
    return {
      image: {
        width: "auto",
        height: "auto"
      } as ImageData
    }
  },

  computed: {
    extractAltText(): string {
      if (this.block && this.block.innerHtml) {
        const matchAltFromInnerHTMLRegex = new RegExp(/alt="([^"]*?)"/g)
        const matches = this.block.innerHtml.match(matchAltFromInnerHTMLRegex)

        if (matches?.length) {
          return matches[0].slice(5, -1)
        }
      }
      return ""
    },

    imageAttrs(): Record<string, any> {
      const attrs: Record<string, any> = {
        alt: this.image.altText || "image-" + this.imageData.id,
        class: {
          // @ts-ignore
          [this.$style.image]: true,
          "fixed-size": this.imageData.width || this.imageData.height
        },
        style: "--image-width:" + this.image.width + "; --image-height:" + this.image.height + ";"
      }

      if (this.image.width !== "auto") {
        attrs.width = this.image.width
      }
      if (this.image.height !== "auto") {
        attrs.height = this.image.height
      }

      if (!this.lazy && this.blockId === "block-no-0") {
        attrs.fetchpriority = "high"
      }

      return attrs
    }
  },

  methods: {
    replaceUrlS3,

    head(): Record<string, any> | void {
      if (!this.lazy && this.blockId === "block-no-0") {
        if (this.media) {
          return {
            link: [
              {
                rel: "preload",
                as: "image",
                imagesrcset: this.image.sourceUrl
                  ? this.image.sourceUrl
                  : this.replaceUrlS3(this.imageData.url, this.$config.public),
                media: this.media
              }
            ]
          }
        }

        return {
          link: [
            {
              rel: "preload",
              as: "image",
              href: this.image.sourceUrl
                ? this.image.sourceUrl
                : this.replaceUrlS3(this.imageData.url, this.$config.public)
            }
          ]
        }
      }
    },

    async fetch(): Promise<void> {
      if (this.imageData.id && import.meta.server) {
        try {
          const response = await getMediaById(this.$config.public, this.imageData.id, this.idType)

          if (response.data && response.data.mediaItem) {
            const media = response.data.mediaItem
            const selectedSize =
              media.mediaDetails.sizes && this.size && this.size != "full"
                ? media.mediaDetails.sizes.find(size => size.name === this.size)
                : null

            if (selectedSize) {
              this.image.width = selectedSize.width
              this.image.height = selectedSize.height
              this.image.sourceUrl = this.replaceUrlS3(selectedSize.sourceUrl, this.$config.public)
              this.image.srcSet = null
              this.image.altText = media.altText
            } else {
              this.image = media
              this.image.srcSet = this.replaceUrlS3(
                this.image.srcSet as string,
                this.$config.public
              )

              if (media.mediaDetails.sizes && media.mediaDetails.sizes.length > 1) {
                media.mediaDetails.sizes.push({
                  width: this.image.mediaDetails?.width,
                  height: this.image.mediaDetails?.height,
                  sourceUrl: this.image.sourceUrl
                })
              } else {
                this.image.width = this.image.mediaDetails?.width as number
                this.image.height = this.image.mediaDetails?.height as number
                this.image.sourceUrl = this.replaceUrlS3(media.sourceUrl, this.$config.public)
              }
            }
          }
        } catch (err) {
          console.log("Media error", err)
        }
      }

      if (this.imageData.width || this.imageData.height) {
        this.image.width = this.imageData.width
        this.image.height = this.imageData.height
      }
    }
  }
})
</script>

<style lang="scss" module>
.image {
  &:global(.loading) {
    opacity: 0;
    transition: opacity 2s;
    background: transparent
      url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3Csvg width='44' height='44' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='xMidYMid' class='uil-ring'%3E%3Crect x='0' y='0' width='100' height='100' fill='none' class='bk'%3E%3C/rect%3E%3Ccircle cx='50' cy='50' r='45.5' stroke-dasharray='207.26657532058658 78.61835615608459' stroke='%2341a5dd' fill='none' stroke-width='9'%3E%3CanimateTransform attributeName='transform' type='rotate' values='0 50 50;180 50 50;360 50 50;' keyTimes='0;0.5;1' dur='1s' repeatCount='indefinite' begin='0s'%3E%3C/animateTransform%3E%3C/circle%3E%3C/svg%3E")
      no-repeat center center;
  }
}

.image {
  opacity: 1;
}
</style>
